import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as ValidateIcon,
  Send as SendIcon,
  Receipt as CreditNoteIcon,
  MoneyOff as LateFeeIcon,
  Discount as DiscountIcon,
  NotificationsActive as ReminderIcon,
  Warning as OverdueIcon,
  PlayArrow as ActionIcon,
} from '@mui/icons-material';
import {
  useInvoices,
  useValidateInvoice,
  useSendInvoice,
  useCreateCreditNote,
  useApplyLateFee,
  useApplyInvoiceDiscount,
  useSendPaymentReminder,
  useOverdueInvoices,
} from '../../hooks/useBilling';
import SmartDataGrid from '../common/SmartDataGrid';
import { handleApiError } from '../../utils/errorHandler';
import {
  SUPPORTED_CURRENCIES,
  getCurrencySymbol,
} from '../../utils/invoice-pdf-general-section-utilities';

const InvoiceWorkflowPanel = () => {
  const { t, i18n: _i18n } = useTranslation('business');
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');

  // Format currency using invoice PDF utilities
  const formatCurrency = useCallback((amount, currency = selectedCurrency) => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${(amount || 0).toFixed(2)}`;
  }, [selectedCurrency]);

  // Handle currency change
  const _handleCurrencyChange = useCallback((event) => {
    setSelectedCurrency(event.target.value);
  }, []);

  const [validateDialogOpen, setValidateDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [creditNoteDialogOpen, setCreditNoteDialogOpen] = useState(false);
  const [lateFeeDialogOpen, setLateFeeDialogOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);

  const [validateForm, setValidateForm] = useState({ change_reason: '' });
  const [sendForm, setSendForm] = useState({ email: '', change_reason: '' });
  const [creditNoteForm, setCreditNoteForm] = useState({ amount: 0, reason: '' });
  const [lateFeeForm, setLateFeeForm] = useState({ fee_percentage: 10 });
  const [discountForm, setDiscountForm] = useState({ discount_code: '', discount_amount: 0 });
  const [reminderForm, setReminderForm] = useState({ message: '' });

  const { data: invoicesData, isLoading: invoicesLoading, refetch: refetchInvoices } = useInvoices();
  const { data: overdueData, isLoading: _overdueLoading } = useOverdueInvoices(0);

  const validateMutation = useValidateInvoice();
  const sendMutation = useSendInvoice();
  const creditNoteMutation = useCreateCreditNote();
  const lateFeeMutation = useApplyLateFee();
  const discountMutation = useApplyInvoiceDiscount();
  const reminderMutation = useSendPaymentReminder();

  const invoices = useMemo(() => invoicesData?.items || invoicesData || [], [invoicesData]);
  const overdueInvoices = useMemo(() => overdueData?.items || overdueData || [], [overdueData]);

  const filteredInvoices = useMemo(() => {
    let filtered = invoices;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(invoice =>
        invoice.invoice_number?.toLowerCase().includes(query) ||
        invoice.customer_name?.toLowerCase().includes(query)
      );
    }
    if (statusFilter) {
      filtered = filtered.filter(invoice => invoice.status === statusFilter);
    }
    return filtered;
  }, [invoices, searchQuery, statusFilter]);

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      pending: 'warning',
      sent: 'info',
      partial: 'secondary',
      paid: 'success',
      overdue: 'error',
      refunded: 'warning',
      credit_note: 'secondary',
      cancelled: 'error',
    };
    return colors[status?.toLowerCase()] || 'default';
  };

  const getWorkflowSteps = (status) => {
    const steps = [
      { label: t('invoicing.workflow.steps.draft'), status: 'draft' },
      { label: t('invoicing.workflow.steps.pending'), status: 'pending' },
      { label: t('invoicing.workflow.steps.sent'), status: 'sent' },
      { label: t('invoicing.workflow.steps.paid'), status: 'paid' },
    ];
    const statusIndex = {
      draft: 0,
      pending: 1,
      sent: 2,
      partial: 2,
      paid: 3,
      overdue: 2,
      refunded: 3,
      credit_note: 3,
      cancelled: -1,
    };
    return { steps, activeStep: statusIndex[status?.toLowerCase()] ?? 0 };
  };

  const handleValidate = async () => {
    try {
      await validateMutation.mutateAsync({ invoiceId: selectedInvoice.id, changeReason: validateForm.change_reason });
      enqueueSnackbar(t('invoicing.workflow.messages.validateSuccess'), { variant: 'success' });
      setValidateDialogOpen(false);
      setValidateForm({ change_reason: '' });
      refetchInvoices();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error validating invoice:');
    }
  };

  const handleSend = async () => {
    try {
      await sendMutation.mutateAsync({ invoiceId: selectedInvoice.id, email: sendForm.email, changeReason: sendForm.change_reason });
      enqueueSnackbar(t('invoicing.workflow.messages.sendSuccess'), { variant: 'success' });
      setSendDialogOpen(false);
      setSendForm({ email: '', change_reason: '' });
      refetchInvoices();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error sending invoice:');
    }
  };

  const handleCreateCreditNote = async () => {
    try {
      await creditNoteMutation.mutateAsync({ invoiceId: selectedInvoice.id, amount: creditNoteForm.amount, reason: creditNoteForm.reason });
      enqueueSnackbar(t('invoicing.workflow.messages.creditNoteSuccess'), { variant: 'success' });
      setCreditNoteDialogOpen(false);
      setCreditNoteForm({ amount: 0, reason: '' });
      refetchInvoices();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error creating credit note:');
    }
  };

  const handleApplyLateFee = async () => {
    try {
      await lateFeeMutation.mutateAsync({ invoiceId: selectedInvoice.id, feePercentage: lateFeeForm.fee_percentage });
      enqueueSnackbar(t('invoicing.workflow.messages.lateFeeSuccess'), { variant: 'success' });
      setLateFeeDialogOpen(false);
      setLateFeeForm({ fee_percentage: 10 });
      refetchInvoices();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error applying late fee:');
    }
  };

  const handleApplyDiscount = async () => {
    try {
      await discountMutation.mutateAsync({ invoiceId: selectedInvoice.id, discountCode: discountForm.discount_code, discountAmount: discountForm.discount_amount });
      enqueueSnackbar(t('invoicing.workflow.messages.discountSuccess'), { variant: 'success' });
      setDiscountDialogOpen(false);
      setDiscountForm({ discount_code: '', discount_amount: 0 });
      refetchInvoices();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error applying discount:');
    }
  };

  const handleSendReminder = async () => {
    try {
      await reminderMutation.mutateAsync({ invoiceId: selectedInvoice.id, message: reminderForm.message });
      enqueueSnackbar(t('invoicing.workflow.messages.reminderSuccess'), { variant: 'success' });
      setReminderDialogOpen(false);
      setReminderForm({ message: '' });
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error sending reminder:');
    }
  };

  const invoiceColumns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { 
      field: 'invoice_number', 
      headerName: t('invoicing.workflow.columns.invoiceNumber'), 
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">{params.value || `INV-${params.row.id}`}</Typography>
      ),
    },
    { 
      field: 'customer_name', 
      headerName: t('invoicing.workflow.columns.customer'), 
      flex: 1,
    },
    { 
      field: 'status', 
      headerName: t('invoicing.workflow.columns.status'), 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={t(`invoicing.status.${params.value?.toLowerCase()}`, params.value)} 
          size="small" 
          color={getStatusColor(params.value)}
        />
      ),
    },
    { 
      field: 'total_amount', 
      headerName: t('invoicing.workflow.columns.amount'), 
      width: 120,
      renderCell: (params) => formatCurrency(params.value, params.row.currency || selectedCurrency),
    },
    { 
      field: 'due_date', 
      headerName: t('invoicing.workflow.columns.dueDate'), 
      width: 120,
    },
  ];

  const canValidate = selectedInvoice?.status === 'draft';
  const canSend = selectedInvoice?.status === 'pending';
  const canApplyLateFee = selectedInvoice?.status === 'overdue' || selectedInvoice?.status === 'sent';
  const canApplyDiscount = selectedInvoice?.status === 'draft' || selectedInvoice?.status === 'pending';
  const canSendReminder = selectedInvoice?.status === 'sent' || selectedInvoice?.status === 'overdue';
  const canCreateCreditNote = selectedInvoice?.status === 'paid' || selectedInvoice?.status === 'partial';

  const statusOptions = ['draft', 'pending', 'sent', 'partial', 'paid', 'overdue', 'refunded', 'credit_note', 'cancelled'];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{t('invoicing.workflow.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('invoicing.workflow.subtitle')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('invoicing.workflow.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('invoicing.workflow.filterByStatus')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('invoicing.workflow.filterByStatus')}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all', 'All')}</MenuItem>
              {statusOptions.map(status => (
                <MenuItem key={status} value={status}>
                  {t(`invoicing.status.${status}`, status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetchInvoices()}>
            {t('common.refresh', 'Refresh')}
          </Button>
        </Box>
      </Box>

      {overdueInvoices.length > 0 && (
        <Alert severity="warning" sx={{ m: 2 }}>
          <Typography variant="body2">
            {t('invoicing.workflow.overdueAlert', { count: overdueInvoices.length })}
          </Typography>
        </Alert>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex' }}>
        <Box sx={{ width: selectedInvoice ? '50%' : '100%', borderRight: selectedInvoice ? 1 : 0, borderColor: 'divider' }}>
          <SmartDataGrid
            rows={filteredInvoices}
            columns={invoiceColumns}
            loading={invoicesLoading}
            onRowClick={(params) => setSelectedInvoice(params.row)}
            getRowClassName={(params) => params.row.id === selectedInvoice?.id ? 'Mui-selected' : ''}
          />
        </Box>

        {selectedInvoice && (
          <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Typography variant="h6">
                {selectedInvoice.invoice_number || `INV-${selectedInvoice.id}`}
              </Typography>
              <Chip 
                label={t(`invoicing.status.${selectedInvoice.status?.toLowerCase()}`, selectedInvoice.status)} 
                size="small" 
                color={getStatusColor(selectedInvoice.status)}
                sx={{ mt: 1 }}
              />
            </Box>

            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>{t('invoicing.workflow.lifecycle')}</Typography>
              <Stepper activeStep={getWorkflowSteps(selectedInvoice.status).activeStep} orientation="horizontal" sx={{ mb: 3 }}>
                {getWorkflowSteps(selectedInvoice.status).steps.map((step, _index) => (
                  <Step key={step.status}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Typography variant="subtitle2" gutterBottom>{t('invoicing.workflow.availableActions')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ValidateIcon />}
                    onClick={() => setValidateDialogOpen(true)}
                    disabled={!canValidate}
                  >
                    {t('invoicing.workflow.actions.validate')}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={() => setSendDialogOpen(true)}
                    disabled={!canSend}
                  >
                    {t('invoicing.workflow.actions.send')}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CreditNoteIcon />}
                    onClick={() => setCreditNoteDialogOpen(true)}
                    disabled={!canCreateCreditNote}
                  >
                    {t('invoicing.workflow.actions.creditNote')}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LateFeeIcon />}
                    onClick={() => setLateFeeDialogOpen(true)}
                    disabled={!canApplyLateFee}
                  >
                    {t('invoicing.workflow.actions.lateFee')}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DiscountIcon />}
                    onClick={() => setDiscountDialogOpen(true)}
                    disabled={!canApplyDiscount}
                  >
                    {t('invoicing.workflow.actions.discount')}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ReminderIcon />}
                    onClick={() => setReminderDialogOpen(true)}
                    disabled={!canSendReminder}
                  >
                    {t('invoicing.workflow.actions.reminder')}
                  </Button>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>{t('invoicing.workflow.invoiceDetails')}</Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('invoicing.workflow.columns.customer')}</Typography>
                    <Typography variant="body2">{selectedInvoice.customer_name || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('invoicing.workflow.columns.amount')}</Typography>
                    <Typography variant="body2">{formatCurrency(selectedInvoice.total_amount, selectedInvoice.currency)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('invoicing.workflow.columns.dueDate')}</Typography>
                    <Typography variant="body2">{selectedInvoice.due_date || '-'}</Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Dialog open={validateDialogOpen} onClose={() => setValidateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.workflow.dialogs.validate.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">{t('invoicing.workflow.dialogs.validate.info')}</Alert>
            <TextField
              label={t('invoicing.workflow.dialogs.validate.reason')}
              value={validateForm.change_reason}
              onChange={(e) => setValidateForm({ change_reason: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidateDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleValidate}>{t('invoicing.workflow.actions.validate')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.workflow.dialogs.send.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">{t('invoicing.workflow.dialogs.send.info')}</Alert>
            <TextField
              label={t('invoicing.workflow.dialogs.send.email')}
              type="email"
              value={sendForm.email}
              onChange={(e) => setSendForm({ ...sendForm, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('invoicing.workflow.dialogs.send.reason')}
              value={sendForm.change_reason}
              onChange={(e) => setSendForm({ ...sendForm, change_reason: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleSend}>{t('invoicing.workflow.actions.send')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={creditNoteDialogOpen} onClose={() => setCreditNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.workflow.dialogs.creditNote.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">{t('invoicing.workflow.dialogs.creditNote.info')}</Alert>
            <TextField
              label={t('invoicing.workflow.dialogs.creditNote.amount')}
              type="number"
              value={creditNoteForm.amount}
              onChange={(e) => setCreditNoteForm({ ...creditNoteForm, amount: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label={t('invoicing.workflow.dialogs.creditNote.reason')}
              value={creditNoteForm.reason}
              onChange={(e) => setCreditNoteForm({ ...creditNoteForm, reason: e.target.value })}
              fullWidth
              multiline
              rows={2}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditNoteDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleCreateCreditNote}>{t('invoicing.workflow.actions.creditNote')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={lateFeeDialogOpen} onClose={() => setLateFeeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.workflow.dialogs.lateFee.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="warning">{t('invoicing.workflow.dialogs.lateFee.info')}</Alert>
            <TextField
              label={t('invoicing.workflow.dialogs.lateFee.percentage')}
              type="number"
              value={lateFeeForm.fee_percentage}
              onChange={(e) => setLateFeeForm({ fee_percentage: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 0.1 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLateFeeDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" color="warning" onClick={handleApplyLateFee}>{t('invoicing.workflow.actions.lateFee')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={discountDialogOpen} onClose={() => setDiscountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.workflow.dialogs.discount.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">{t('invoicing.workflow.dialogs.discount.info')}</Alert>
            <TextField
              label={t('invoicing.workflow.dialogs.discount.code')}
              value={discountForm.discount_code}
              onChange={(e) => setDiscountForm({ ...discountForm, discount_code: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('invoicing.workflow.dialogs.discount.amount')}
              type="number"
              value={discountForm.discount_amount}
              onChange={(e) => setDiscountForm({ ...discountForm, discount_amount: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleApplyDiscount}>{t('invoicing.workflow.actions.discount')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reminderDialogOpen} onClose={() => setReminderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.workflow.dialogs.reminder.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">{t('invoicing.workflow.dialogs.reminder.info')}</Alert>
            <TextField
              label={t('invoicing.workflow.dialogs.reminder.message')}
              value={reminderForm.message}
              onChange={(e) => setReminderForm({ message: e.target.value })}
              fullWidth
              multiline
              rows={4}
              placeholder={t('invoicing.workflow.dialogs.reminder.placeholder')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleSendReminder}>{t('invoicing.workflow.actions.reminder')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(InvoiceWorkflowPanel);
